<script setup lang="ts">
import { ref, onMounted, toRefs, computed, watch, capitalize } from 'vue';
import type { Language } from '@ilteducation/content-ref-data-types';
import { useDisplay } from 'vuetify';
import { useGamesTheme } from '../composables/use-games-theme';
import type {
  AudioResource,
  KeywordWithTranslations,
  SimpleKeyword,
  SimpleDescription,
  TranslatedKeyword,
} from './types';
import ActiveGame from './components/ActiveGame.vue';
import useA11yLiveFeedback from './composables/useA11yLiveFeedback';

const props = defineProps<{
  t: CallableFunction;
  languages: Language[];
  keywords: KeywordWithTranslations[];
  audioResources: AudioResource[];
  contentRegistryUrl: string;
  defaultLanguage: string;
}>();

const emits = defineEmits<{
  (e: 'close'): void;
  (e: 'language-change', language: string): void;
}>();
const { t, keywords, languages, defaultLanguage } = toRefs(props);

const { smAndUp } = useDisplay();

const activeGameComponent = ref<typeof ActiveGame>();

const { liveRegionRef: restartDiv, giveA11yFeedback } = useA11yLiveFeedback();

const languageOptions = ref<Language[]>([]);

const selectedLanguage = ref<string>(defaultLanguage.value);

const extractLanguageOptions = () => {
  const keywordCountByLanguage: Record<string, number> = {};
  keywords.value
    .filter((keyword) => keyword.active)
    .forEach((keyword) => {
      Object.keys(keyword.translations)
        .filter((lang) => keyword.translations[lang].title && keyword.translations[lang].description)
        .forEach((lang) => {
          keywordCountByLanguage[lang] = (keywordCountByLanguage[lang] ?? 0) + 1;
        });
    });
  languageOptions.value = Object.keys(keywordCountByLanguage)
    .filter((lang) => keywordCountByLanguage[lang] > 1)
    .map((lang) => languages.value.find((l) => l.languageCode === lang))
    .filter((lang): lang is Language => !!lang)
    .sort((a, b) => a.language.localeCompare(b.language));
};

const keywordsByLanguage = (langShortCode: string) =>
  keywords.value
    .filter((keyword) => keyword.active)
    .filter(
      (keyword) =>
        keyword.translations[selectedLanguage.value]?.title &&
        keyword.translations[selectedLanguage.value]?.description,
    )
    .map(
      (filteredKeyword): TranslatedKeyword => ({
        id: filteredKeyword.id,
        translations: filteredKeyword.translations[langShortCode],
      }),
    );

const shuffledKeywords = ref<SimpleKeyword[]>([]);
const shuffledDescriptions = ref<SimpleDescription[]>([]);
const roundIds = ref<string[]>([]);
const wordOrder = ref<string[]>([]);
const descriptionOrder = ref<string[]>([]);
const matchedIds = ref<string[]>([]);

const shuffleArray = <T,>(array: T[]) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
};

const translationFor = (id: string, language = selectedLanguage.value) => {
  const translation = keywords.value.find((keyword) => keyword.id === id)?.translations[language];
  return translation?.title && translation.description ? translation : undefined;
};

const ROUND_SIZE = 6;

const visibleIds = (language = selectedLanguage.value) =>
  roundIds.value.filter((id) => translationFor(id, language)).slice(0, ROUND_SIZE);

const showRound = () => {
  const visible = visibleIds();
  shuffledKeywords.value = wordOrder.value.flatMap((id) => {
    const translation = translationFor(id);
    return translation && visible.includes(id) && !matchedIds.value.includes(id)
      ? [{ id, keyword: translation.title }]
      : [];
  });
  shuffledDescriptions.value = descriptionOrder.value.flatMap((id) => {
    const translation = translationFor(id);
    return translation && visible.includes(id) ? [{ id, description: translation.description }] : [];
  });
};

const fillRound = () => {
  const missingCount = ROUND_SIZE - visibleIds().length;
  const candidates = keywordsByLanguage(selectedLanguage.value)
    .map((keyword) => keyword.id)
    .filter((id) => !roundIds.value.includes(id));
  shuffleArray(candidates);
  candidates.slice(0, missingCount).forEach((id) => {
    roundIds.value.push(id);
    wordOrder.value.splice(Math.floor(Math.random() * (wordOrder.value.length + 1)), 0, id);
    descriptionOrder.value.splice(Math.floor(Math.random() * (descriptionOrder.value.length + 1)), 0, id);
  });
};

const shuffleCurrentRound = () => {
  wordOrder.value = roundIds.value.slice();
  shuffleArray(wordOrder.value);
  descriptionOrder.value = roundIds.value.slice();
  shuffleArray(descriptionOrder.value);
  matchedIds.value = [];
  showRound();
};

const randomize = (languageId: string) => {
  const originalKeywords = keywordsByLanguage(languageId);
  shuffleArray(originalKeywords);
  roundIds.value = originalKeywords.slice(0, ROUND_SIZE).map((keyword) => keyword.id);
  shuffleCurrentRound();
};

const allowStart = computed(() => keywordsByLanguage(selectedLanguage.value).length > 1);
const rtlLanguage = computed<boolean>(() => ['arb', 'ar', 'prs', 'fa', 'fas'].includes(selectedLanguage.value));

const restartGame = () => {
  // Set focus to the game title
  const gameTitle = document.getElementById('game-title');
  if (gameTitle) {
    gameTitle.focus();
  }
  shuffledDescriptions.value = [];
  shuffledKeywords.value = [];
  activeGameComponent.value?.clearWords();
  randomize(selectedLanguage.value);
  giveA11yFeedback(t.value('games.match_the_pairs.feedback.restart_game'));
};
const playAgain = () => {
  document.getElementById('game-title')?.focus();
  activeGameComponent.value?.clearWords();
  shuffleCurrentRound();
  giveA11yFeedback(t.value('games.match_the_pairs.feedback.restart_game'));
};

watch(selectedLanguage, (_, previousLanguage) => {
  const remainingIds = shuffledKeywords.value.map((word) => word.id);
  matchedIds.value.push(
    ...visibleIds(previousLanguage).filter((id) => !matchedIds.value.includes(id) && !remainingIds.includes(id)),
  );
  fillRound();
  showRound();
  activeGameComponent.value?.setMatches(
    matchedIds.value.flatMap((id) => {
      const translation = translationFor(id);
      return translation ? [{ id, keyword: translation.title }] : [];
    }),
  );
});
const close = () => {
  emits('close');
};

const onLanguageChange = () => {
  emits('language-change', selectedLanguage.value);
};

onMounted(() => {
  extractLanguageOptions();
  randomize(defaultLanguage.value);
});

const { theme } = useGamesTheme();

defineExpose({ close });
</script>

<template>
  <v-theme-provider :theme="theme" with-background class="bg-surface h-100">
    <v-card
      :height="smAndUp ? undefined : '100%'"
      :min-height="smAndUp ? '100%' : undefined"
      class="match-the-pairs-layout d-flex flex-column rounded-0"
    >
      <div class="d-flex align-center justify-space-between ga-4 pt-4 pl-4 pr-4 pr-md-6">
        <div class="d-flex align-center ga-2">
          <v-btn
            icon="fa:fawsb fa-angle-left"
            variant="text"
            size="small"
            :aria-label="t('button_close')"
            @click="close"
          />
          <h1 id="game-title" class="chalkduster text-h5 text-md-h4 font-weight-regular" tabindex="-1">
            {{ t('games.match_the_pairs.title') }}
          </h1>
        </div>
        <v-select
          v-if="allowStart && languageOptions.length > 1"
          v-model="selectedLanguage"
          :items="languageOptions"
          :item-title="(language: Language) => capitalize(language.language)"
          :item-value="(language: Language) => language.languageCode"
          :aria-label="t('games.match_the_pairs.select_language')"
          :density="smAndUp ? 'comfortable' : 'compact'"
          :menu-props="{ style: 'z-index: 2500' }"
          menu-icon="fa:fawsb fa-angle-down"
          variant="outlined"
          rounded="md"
          hide-details
          width="177"
          class="mtp-language-select chalkboard flex-grow-0"
          @update:modelValue="onLanguageChange"
        />
      </div>
      <v-alert
        v-if="!allowStart"
        icon="mdi-information"
        variant="outlined"
        class="ma-8"
        :text="t('games.match_the_pairs.start_game_condition')"
      />
      <ActiveGame
        v-if="allowStart"
        ref="activeGameComponent"
        :t="t"
        :words="shuffledKeywords"
        :descriptions="shuffledDescriptions"
        :audio-resources="selectedLanguage === 'sv' ? audioResources : []"
        :content-registry-url="contentRegistryUrl"
        :rtl-language="rtlLanguage"
        @restart="playAgain"
        @shuffle="restartGame"
        @close="close"
      />
    </v-card>
  </v-theme-provider>
  <!-- Accessible live region for screen reader feedback -->
  <div ref="restartDiv" class="d-sr-only" role="log" aria-live="polite" aria-atomic="true"></div>
</template>

<style lang="scss">
.match-the-pairs-layout {
  background-image: var(--v-games-background-image);
  background-size: cover;
  background-attachment: fixed;
  min-height: 100%;
  font-family: 'Chalkboard', 'Comic Sans MS', sans-serif;

  [class*='text-'] {
    font-family: inherit;
  }

  .chalkduster {
    font-family: 'Chalkduster', sans-serif;
  }

  .chalkboard {
    font-family: 'Chalkboard', 'Comic Sans MS', sans-serif;
  }
}

.mtp-language-select .v-select__menu-icon {
  font-size: 1rem;
}
</style>
